import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
// import { TOPIC_DATA } from '../constants/topics.constant';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { TopicsApiResponse, WebhookMonitorResponse } from '../interfaces/calendely.interface';

// Minimal shape for optional runtime configuration supplied by the server
interface RuntimeConfig {
  VITE_API_BASEURL?: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class CalendlyService {
  // runtime config is loaded asynchronously at startup; defaults to environment values
  private runtimeConfig: RuntimeConfig | null = null;
  private configLoaded = false;
  private configPromise: Promise<void> | null = null;

  // compute base URL at use-time so runtime overrides (if any) are respected
  private get baseUrl(): string {
    return (
      (this.runtimeConfig?.VITE_API_BASEURL as string | undefined) ||
      '' // fallback to empty string; runtime config is expected
    );
  }

  // FULL URLs created on demand
  private get topicUrl(): string {
    console.log('Base URL:', this.baseUrl);
    return `${this.baseUrl}/api/v1/topic-subtopic`;
  }

  private get webhookCheckUrl(): string {
    return `${this.baseUrl}/api/v1/calendly/webhook/monitor`;
  }

  constructor(private readonly http: HttpClient) {
    // Initialize config loading
    this.initConfig();
  }

  private initConfig(): void {
    if (!this.configPromise) {
      this.configPromise = loadRuntimeConfig()
        .then((cfg) => {
          this.runtimeConfig = cfg || null;
          this.configLoaded = true;
          console.log('[CalendlyService] Runtime config loaded:', this.runtimeConfig);
        })
        .catch((err) => {
          console.warn('[CalendlyService] failed to load runtime config', err);
          this.configLoaded = true; // Mark as loaded even if failed
        });
    }
  }

  // Ensure config is loaded before making API calls
  async ensureConfigLoaded(): Promise<void> {
    if (this.configLoaded) return;
    await this.configPromise;
  }

  // getTopics(brandName: string): Observable<TopicsApiResponse> {
  //   console.log(brandName);
  // const mockResponse: TopicsApiResponse = TOPIC_DATA;
  // return of(mockResponse);
  // }

  getTopics(brandName: string): Observable<TopicsApiResponse> {
    const params = new HttpParams().set('brand', brandName);
    return this.http.get<TopicsApiResponse>(this.topicUrl, { params }).pipe(
      map((res) => res),
      catchError((err: unknown) => {
        console.error('[CalendlyService] getTopics failed', err);
        // Return an empty TopicsApiResponse fallback so the Observable type remains Observable<TopicsApiResponse>
        return of({} as TopicsApiResponse);
      })
    );
  }
  
  validateWebhook(brandName: string): Observable<WebhookMonitorResponse> {

    return this.http.post<WebhookMonitorResponse>(this.webhookCheckUrl, { 'brand': brandName }).pipe(
      catchError((err: unknown) => {
        console.error('[CalendlyService] postWebhookMonitor failed', err);
        // Return a safe fallback value so callers have a predictable shape
        return of({ success: false, message: 'Request failed' } as WebhookMonitorResponse);
      })
    );
  }
}

async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  let fileConfig: RuntimeConfig = {};
  try {
    const res = await fetch("/config/config.json", { method: "GET" });
    if (res.ok) {
      fileConfig = await res.json();
    } else {
      console.warn("Runtime config fetch failed (status)", res.status);
      // Return empty config for 404 - it's expected in dev/test environments
    }
  } catch (err) {
    console.warn("Runtime config fetch error", err);
    // Return empty config on error - don't block the app
  }
  return fileConfig;
}